import type { ComponentType } from 'react';

import type { IconProps } from '@chakra-ui/react';

import { BookIcon, CircleCheckIcon, ExpandIcon, ReticleIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export type SocietalImpactPillarTheme = 'blue' | 'purple';

export type SocietalImpactPillar = {
  id: string;
  theme: SocietalImpactPillarTheme;
  heading: string;
  body: string;
  pictogramSrc: string;
  pictogramAlt: string;
};

export type SocietalImpactProgramCard = {
  id: string;
  Icon: ComponentType<IconProps>;
  title: string;
  body: string;
};

export const societalImpactContent = {
  pretitle: 'Societal Impact',
  heading: 'Our societal impact solutions for governments and partners',
  bottomHeading: 'Flexible program models to scale impact',
  bottomIntro:
    'Udacity Societal Impact solution offers a suite of programs designed to develop digital skills at every level, from broad awareness to career-ready expertise:',
  customSolutionsTitle:
    'We design custom solutions to meet national priorities and workforce needs. Customized solutions include:',
};

export const societalImpactPillars: SocietalImpactPillar[] = [
  {
    id: 'nationwide',
    theme: 'blue',
    heading: 'Nationwide digital upskilling',
    body: 'Enable large populations to gain foundational digital skills and technology fluency, building awareness and preparing citizens for the digital economy.',
    pictogramSrc: '/images/government/societal-impact/pictogram-nationwide.svg',
    pictogramAlt: '',
  },
  {
    id: 'targeted',
    theme: 'purple',
    heading: 'Targeted technical upskilling',
    body: 'Provide deeper technical training to a selected group of learners in high-demand fields such as AI, Data, Programming, and Digital Marketing to prepare them for employment or freelancing opportunities.',
    pictogramSrc: '/images/government/societal-impact/pictogram-targeted.svg',
    pictogramAlt: '',
  },
];

// Card order is the desktop order from Figma — Foundational → Hybrid → Advanced → Connect lite.
// The mobile Figma frame happens to reorder Advanced/Hybrid; we intentionally ship the desktop
// order on both breakpoints so a single source array drives both layouts.
export const societalImpactProgramCards: SocietalImpactProgramCard[] = [
  {
    id: 'foundational',
    Icon: BookIcon,
    title: 'Foundational tech programs',
    body: 'Broad-access, short courses to build foundational digital skills at scale.',
  },
  {
    id: 'hybrid',
    Icon: CircleCheckIcon,
    title: 'Hybrid solutions',
    body: 'Multi-phase, large-scale initiatives combining foundational and advanced learning, designed to identify top talent.',
  },
  {
    id: 'advanced',
    Icon: ReticleIcon,
    title: 'Advanced expert-led models',
    body: 'Targeted learning with hands-on projects and mentor support to develop career-ready technical expertise.',
  },
  {
    id: 'connect-lite',
    Icon: ExpandIcon,
    title: 'Connect lite solutions',
    body: 'High-volume, lower-touch version of Connect for wider reach with streamlined mentorship.',
  },
];

export const societalImpactCustomSolutionBullets: string[] = [
  'Specialized curricula aligned with economic priorities',
  'Localized content and language adaptations',
  'Career-focused pathways, from freelancing to entrepreneurship',
  'Flexible mentorship models and program timelines',
];
